// Pointer handling for the gutter numbers (design §6.2):
//   tap        → select that paragraph
//   drag       → extend the selection across a contiguous range
//   long-press → lift the paragraph; drag to reorder; release to drop
//
// One pointer at a time. The gutter has `touch-action: none`, so the browser
// never scrolls from it; scrolling happens on the text column.

import { type PointerEvent as ReactPointerEvent, useCallback, useRef, useState } from "react";

const LONG_PRESS_MS = 500;
const MOVE_SLOP_PX = 8;

export interface GutterCallbacks {
  onTap: (index: number) => void;
  onRange: (from: number, to: number) => void;
  onDrop: (from: number, to: number) => void;
}

type Mode = "idle" | "range" | "lift";

interface Gesture {
  index: number;
  y: number;
  timer: number | null;
  mode: Mode;
}

export function useGutter(
  rows: React.RefObject<(HTMLElement | null)[]>,
  callbacks: GutterCallbacks,
) {
  const [lifted, setLifted] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const gesture = useRef<Gesture | null>(null);
  const cb = useRef(callbacks);
  cb.current = callbacks;

  const rowAtY = useCallback(
    (y: number): number => {
      let best = -1;
      let bestDistance = Number.POSITIVE_INFINITY;
      rows.current?.forEach((el, i) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        const distance = y < r.top ? r.top - y : y > r.bottom ? y - r.bottom : 0;
        if (distance < bestDistance) {
          best = i;
          bestDistance = distance;
        }
      });
      return best;
    },
    [rows],
  );

  /** Index the lifted row would land at: the number of other rows whose
   *  vertical midpoint is above the pointer. Matches `move(doc, id, to)`. */
  const dropAtY = useCallback(
    (y: number, dragging: number): number => {
      let count = 0;
      rows.current?.forEach((el, i) => {
        if (!el || i === dragging) return;
        const r = el.getBoundingClientRect();
        if ((r.top + r.bottom) / 2 < y) count++;
      });
      return count;
    },
    [rows],
  );

  const clear = useCallback(() => {
    const g = gesture.current;
    if (g?.timer != null) window.clearTimeout(g.timer);
    gesture.current = null;
    setLifted(null);
    setDropIndex(null);
  }, []);

  const handlers = useCallback(
    (index: number) => ({
      onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
        if (gesture.current) return;
        e.preventDefault();
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          // Capture is a nicety: keeps moves arriving if the pointer leaves the
          // gutter. Some pointers (synthetic, or already released) refuse it.
        }
        const timer = window.setTimeout(() => {
          const g = gesture.current;
          if (g && g.mode === "idle") {
            g.mode = "lift";
            setLifted(index);
            setDropIndex(index);
          }
        }, LONG_PRESS_MS);
        gesture.current = { index, y: e.clientY, timer, mode: "idle" };
      },
      onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
        const g = gesture.current;
        if (!g) return;
        if (g.mode === "idle") {
          if (Math.abs(e.clientY - g.y) <= MOVE_SLOP_PX) return;
          if (g.timer != null) window.clearTimeout(g.timer);
          g.timer = null;
          g.mode = "range";
        }
        if (g.mode === "range") {
          const i = rowAtY(e.clientY);
          if (i >= 0) cb.current.onRange(g.index, i);
        } else {
          setDropIndex(dropAtY(e.clientY, g.index));
        }
      },
      onPointerUp: (e: ReactPointerEvent<HTMLElement>) => {
        const g = gesture.current;
        if (!g) return;
        if (g.mode === "idle") cb.current.onTap(g.index);
        else if (g.mode === "lift") cb.current.onDrop(g.index, dropAtY(e.clientY, g.index));
        clear();
      },
      onPointerCancel: clear,
    }),
    [clear, dropAtY, rowAtY],
  );

  return { handlers, lifted, dropIndex };
}
